import type { SupSingleVaultV3 } from "@/types";
import React, { useMemo } from "react";

import { useStoredDataDispatch } from "@/store";
import { SupTooltip } from "@repo/core-ui";
import config from "@/../config/dapp.config";
import useRewardSettings from "@/hooks/data/useRewardSettings";
import { getNetVaultApy, getVaultApy, isEmptyAmount } from "@/utils";
import { twMerge } from "tailwind-merge";
import UiLabelPlaceholder from "./Placeholder";
import moment from "moment";

export type SupDicApyPeriod = "day" | "week" | "month";

interface UiLabelApyProps {
  vault?: SupSingleVaultV3;
  value?: number;
  isBold?: boolean;
  unwrapped?: boolean;
  hideTooltip?: boolean;
  className?: string;
  useNetApy?: boolean;
  period?: SupDicApyPeriod;
  noAction?: boolean;
  useFallback?: boolean;
}

export function getEmptyLabel(vault: SupSingleVaultV3 | undefined, period: SupDicApyPeriod) {
  if (vault === undefined) {
    return "N/A";
  }
  const differenceInSeconds = moment().diff(vault.created_at);
  switch (period) {
    case "day":
      return differenceInSeconds / 1000 < 86400 ? "NEW" : "N/A";
    case "week":
      return differenceInSeconds / 1000 < 86400 * 7 ? "NEW" : "N/A";
    default:
      return differenceInSeconds / 1000 < 86400 * 30 ? "NEW" : "N/A";
  }
}

export default function UiLabelApy({
  vault,
  value,
  isBold = false,
  unwrapped = false,
  hideTooltip = false,
  className,
  useNetApy = false,
  period = "month",
  noAction = false,
  useFallback = false,
}: UiLabelApyProps) {
  const rewardSettings = useRewardSettings();
  const storedDispatch = useStoredDataDispatch();

  const chosenValue = useMemo(() => {
    if (!vault) {
      return value;
    }
    return useNetApy ? getNetVaultApy(vault, rewardSettings, period, useFallback) : getVaultApy(vault, period, useFallback);
  }, [period, rewardSettings, useFallback, useNetApy, value, vault]);

  if (chosenValue === undefined || isEmptyAmount(chosenValue)) {
    return (
      <UiLabelPlaceholder variant={useNetApy ? "regular" : "yellow"} unwrapped={unwrapped}>
        {getEmptyLabel(vault, period)}
      </UiLabelPlaceholder>
    );
  }

  const allowAction = !noAction;

  const handleRewards = () => {
    allowAction && storedDispatch({ type: "setOverlay", overlay: "rewards-apy" });
  };

  const apyValue = `${chosenValue.toFixed(config.decimalsApyRoundingLimit)}%`;

  const apyElement = unwrapped ? (
    apyValue
  ) : (
    <span className={twMerge(isBold && "font-bold", "font-sans text-[16px] font-normal leading-none tracking-normal antialiased", className)}>{apyValue}</span>
  );

  return hideTooltip ? (
    apyElement
  ) : (
    <SupTooltip unmount portal content={`${chosenValue}%`} color="primary" placement="top-end">
      <div className={twMerge("inline-flex flex-none justify-center", allowAction && "cursor-pointer")} onClick={handleRewards}>
        {apyElement}
      </div>
    </SupTooltip>
  );
}
